import { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { useReactivateBewerber } from '../../api/bewerber';
import { BewerberStatus, BewerberStatusConfig, type Bewerber } from '../../types/bewerber';

interface ReactivateModalProps {
  isOpen: boolean;
  onClose: () => void;
  bewerber: Bewerber;
  onSuccess?: () => void;
}

export function ReactivateModal({
  isOpen,
  onClose,
  bewerber,
  onSuccess,
}: ReactivateModalProps) {
  const reactivateMutation = useReactivateBewerber();
  const [error, setError] = useState<string | null>(null);

  const targetStatus = bewerber.wasEverGeeignet
    ? BewerberStatus.GEEIGNET
    : BewerberStatus.NEU;

  const targetStatusConfig = BewerberStatusConfig[targetStatus];

  const handleReactivate = async () => {
    setError(null);
    try {
      await reactivateMutation.mutateAsync(bewerber.id);
      toast.success(`Bewerber wurde reaktiviert (Status: ${targetStatusConfig.label})`);
      onClose();
      onSuccess?.();
    } catch (err) {
      setError('Fehler beim Reaktivieren des Bewerbers');
      toast.error('Fehler beim Reaktivieren des Bewerbers');
      console.error('Failed to reactivate bewerber:', err);
    }
  };

  if (!isOpen) return null;

  const bewerberName = `${bewerber.firstName} ${bewerber.lastName}`;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Bewerber reaktivieren
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  <span className="font-medium">{bewerberName}</span> wird reaktiviert und wieder in den Bewerbungsprozess aufgenommen.
                </p>

                {error && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                  <p className="text-sm text-gray-700">
                    Der Bewerber wird auf folgenden Status gesetzt:
                  </p>
                  <div className="mt-2 flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${targetStatusConfig.bgColor} ${targetStatusConfig.color}`}>
                      {targetStatusConfig.label}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      {bewerber.wasEverGeeignet
                        ? '(war bereits als geeignet eingestuft)'
                        : '(war noch nicht als geeignet eingestuft)'}
                    </span>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex">
                    <svg className="h-5 w-5 text-blue-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-blue-700">
                      Der Bewerber wird als "Reaktiviert" markiert und diese Änderung wird in der Historie protokolliert.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleReactivate}
              disabled={reactivateMutation.isPending}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {reactivateMutation.isPending ? 'Wird reaktiviert...' : 'Reaktivieren'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
